import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useLicenseTypeForm } from "@/hooks/use-license-type-form";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import ErrorComponent from "../ui/error";
import LoadingScreen from "../ui/loading-screen";
import { LicenseTypeForm } from "./license-type-form";

interface LicenseTypeDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  licenseTypeId: string | null;
  duplicateLicenseTypeData?: any;
}

export function LicenseTypeDetailsSheet({
  isOpen,
  onOpenChange,
  licenseTypeId,
  duplicateLicenseTypeData,
}: LicenseTypeDetailsSheetProps) {
  const { licenseTypeData, isLoading, error, handleSubmit } =
    useLicenseTypeForm(licenseTypeId);

  // Use duplicate data if available, otherwise use fetched data
  const formData = duplicateLicenseTypeData || licenseTypeData;

  const handleCancel = () => {
    onOpenChange(false);
  };

  let errorTitle: string | null = null;
  let errorMessage: string | null = null;
  if (error) {
    errorTitle = getQueryErrorHeading(error);
    errorMessage = getQueryErrorMessage(error);
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            {duplicateLicenseTypeData
              ? "Duplicate License Type"
              : !licenseTypeId
                ? "Add New License Type"
                : "Edit License Type"}
          </SheetTitle>
          <SheetDescription>
            {duplicateLicenseTypeData
              ? "Create a new license type based on an existing one."
              : !licenseTypeId
                ? "Create a new license type with basic configuration."
                : "Edit license type information and status."}
          </SheetDescription>
        </SheetHeader>
        <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <LoadingScreen />
            </div>
          ) : errorTitle && errorMessage ? (
            <ErrorComponent title={errorTitle} message={errorMessage} />
          ) : formData ? (
            <LicenseTypeForm
              key={
                licenseTypeId ||
                (duplicateLicenseTypeData ? "duplicate" : "new")
              }
              licenseTypeId={licenseTypeId}
              licenseTypeData={formData}
              onSubmit={async (data) => {
                try {
                  await handleSubmit(data);
                  onOpenChange(false);
                } catch (error) {
                  // Error is already handled with toast in the hook
                }
              }}
              onCancel={handleCancel}
            />
          ) : (
            <ErrorComponent
              title="No Data Available"
              message="Unable to load license type data. Please try again."
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
